import { AI, WORLD, hueDistance } from '../constants';
import type { FoodField } from '../entities/Food';
import type { Snake } from '../entities/Snake';
import { mulberry32 } from '../utils/rng';

/** Clamp a single world-space coordinate to the AI's inner steerable
 *  box (WORLD edges minus `AI.EDGE_MARGIN`). Single-axis so callers can
 *  call it on both x and y independently. */
function clampInner(c: number): number {
  const lo = AI.EDGE_MARGIN;
  const hi = WORLD.SIZE - AI.EDGE_MARGIN;
  return c < lo ? lo : c > hi ? hi : c;
}

interface AIBrainState {
  cooldown: number;
  wanderJitter: number;
  rng: () => number;
  /**
   * Encirclement spin direction (+1 = CCW, -1 = CW). Locked per bot so a
   * single chase doesn't oscillate between left- and right-loops as the
   * target moves.
   */
  encircleDir: 1 | -1;
}

/**
 * SnakeAI — utility-style decision module.
 *
 * Every DECISION_INTERVAL seconds each bot picks one of:
 *   - flee from the nearest snake whose mass > self.mass * THREAT_MASS_MULT,
 *   - chase the nearest snake whose mass < self.mass / CHASE_MASS_MULT,
 *   - seek the nearest pellet, weighted toward its own team color,
 *   - wander toward map-center when no targets exist.
 *
 * Output is written to the bot's `steerTargetRaw` (the snake smooths it
 * internally for fluid curves). Boost is set directly.
 */
export class SnakeAI {
  private readonly brains = new Map<number, AIBrainState>();

  attach(bot: Snake): void {
    const rng = mulberry32(bot.id * 2654435761);
    this.brains.set(bot.id, {
      cooldown: 0,
      wanderJitter: 0,
      rng,
      encircleDir: rng() < 0.5 ? 1 : -1,
    });
  }

  detach(botId: number): void {
    this.brains.delete(botId);
  }

  step(bot: Snake, dt: number, allSnakes: Snake[], food: FoodField, player: Snake): void {
    const brain = this.brains.get(bot.id);
    if (!brain) return;

    brain.cooldown -= dt;
    if (brain.cooldown <= 0) {
      brain.cooldown = AI.DECISION_INTERVAL * (0.7 + brain.rng() * 0.6);
      this.decide(bot, brain, allSnakes, food, player);
    } else {
      this.refreshFleeIfNeeded(bot, allSnakes);
    }

    // Small per-frame wander noise — organic motion when idle.
    brain.wanderJitter += (brain.rng() - 0.5) * AI.WANDER_TURN_RATE * dt;
    brain.wanderJitter *= 0.97;

    // Apply wander as a perpendicular nudge to the steer vector.
    const head = bot.head;
    const dx = bot.steerTargetRaw.x - head.x;
    const dy = bot.steerTargetRaw.y - head.y;
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;
    bot.steerTargetRaw.x += px * brain.wanderJitter * 14;
    bot.steerTargetRaw.y += py * brain.wanderJitter * 14;

    // EDGE PULL-BACK — if the bot's HEAD is in the soft border band,
    // unconditionally bias the steer back toward the center. This runs
    // every frame (not just on decision ticks) so a bot that drifted to
    // the edge while a target was elsewhere gets corrected immediately.
    // We add it as a vector blend rather than overwrite so an active
    // chase/flee target still dominates — the pull is a *bias*, not a
    // hard override.
    const margin = AI.EDGE_MARGIN;
    let pullX = 0, pullY = 0;
    if (head.x < margin)               pullX = (margin - head.x) / margin;
    else if (head.x > WORLD.SIZE - margin) pullX = -(head.x - (WORLD.SIZE - margin)) / margin;
    if (head.y < margin)               pullY = (margin - head.y) / margin;
    else if (head.y > WORLD.SIZE - margin) pullY = -(head.y - (WORLD.SIZE - margin)) / margin;
    if (pullX !== 0 || pullY !== 0) {
      // Strength scales with how deep into the edge band we are (0..1).
      // 700-unit pull is enough to overwhelm a stale wander target but
      // gentle enough that a real chase still wins via the dominant
      // forward direction.
      const strength = 700;
      bot.steerTargetRaw.x += pullX * strength;
      bot.steerTargetRaw.y += pullY * strength;
    }

    // Final clamp so the raw target never sits in the impossible-to-
    // reach zone outside the playable inner box. The snake's smoothed
    // steer will glide toward this clamped point.
    bot.steerTargetRaw.x = clampInner(bot.steerTargetRaw.x);
    bot.steerTargetRaw.y = clampInner(bot.steerTargetRaw.y);
  }

  // ---------------------------------------------------------------------------
  // Decision logic
  // ---------------------------------------------------------------------------

  private decide(
    bot: Snake,
    brain: AIBrainState,
    snakes: Snake[],
    food: FoodField,
    player: Snake,
  ): void {
    if (this.tryFlee(bot, snakes)) return;
    if (this.tryChase(bot, snakes)) return;
    // Hunters skip the food layer entirely — pure prey focus.
    if (bot.personality === 'hunter') {
      this.wander(bot, brain, player);
      return;
    }
    if (this.trySeekFood(bot, food)) return;
    this.wander(bot, brain, player);
  }

  /**
   * Personality-scaled multipliers.
   *   aggressive: less likely to flee, more likely to chase
   *   cautious:   more likely to flee, less likely to chase
   *   hunter:     similar to aggressive but with hard food-ignore
   *   normal:     base AI constants
   */
  private threatMultFor(bot: Snake): number {
    switch (bot.personality) {
      case 'aggressive': return AI.THREAT_MASS_MULT * 1.3;
      case 'cautious':   return AI.THREAT_MASS_MULT * 0.85;
      case 'hunter':     return AI.THREAT_MASS_MULT * 1.25;
      default:           return AI.THREAT_MASS_MULT;
    }
  }
  private chaseMultFor(bot: Snake): number {
    switch (bot.personality) {
      case 'aggressive': return AI.CHASE_MASS_MULT * 0.75; // chase even slightly smaller
      case 'cautious':   return AI.CHASE_MASS_MULT * 1.5;  // only chase much smaller
      case 'hunter':     return AI.CHASE_MASS_MULT * 0.8;
      default:           return AI.CHASE_MASS_MULT;
    }
  }

  private tryFlee(bot: Snake, snakes: Snake[]): boolean {
    const head = bot.head;
    let worst: Snake | null = null;
    let worstDist2 = Infinity;
    const rangeSq = AI.PERCEPTION_RANGE * AI.PERCEPTION_RANGE;
    const threatMult = this.threatMultFor(bot);

    for (let i = 0; i < snakes.length; i++) {
      const o = snakes[i]!;
      if (!o.alive || o.id === bot.id) continue;
      if (o.mass < bot.mass * threatMult) continue;
      const dx = o.head.x - head.x;
      const dy = o.head.y - head.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < worstDist2 && d2 < rangeSq) {
        worstDist2 = d2;
        worst = o;
      }
    }

    if (!worst) {
      bot.boosting = false;
      return false;
    }

    // Steer away — straight radial flee, but offset slightly perpendicular
    // so the bot doesn't run in a perfectly predictable line (makes them
    // harder to head off).
    const dx = head.x - worst.head.x;
    const dy = head.y - worst.head.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    // Defensive boost: trigger earlier and more aggressively for cautious
    // personalities. Hunters / aggressive bots only boost when truly close.
    const boostRangeFactor =
      bot.personality === 'cautious'   ? 0.65 :
      bot.personality === 'aggressive' ? 0.40 :
      bot.personality === 'hunter'     ? 0.40 :
                                          0.55;
    // Mass gate so tiny bots without enough fuel don't waste mass on boost
    // (would shrink them faster than the threat itself).
    const canAffordBoost = bot.mass > 320;
    bot.boosting = canAffordBoost && worstDist2 < (AI.PERCEPTION_RANGE * boostRangeFactor) ** 2;
    // Perpendicular sidestep proportional to threat proximity — lets the
    // flee feel like a sharp evasive turn rather than a straight line.
    const proximity = 1 - Math.min(1, Math.sqrt(worstDist2) / AI.PERCEPTION_RANGE);
    const sideSign = ((bot.id * 73856093) & 2) === 0 ? 1 : -1;
    const sideMag = 220 * proximity * sideSign;
    bot.steerTargetRaw.x = head.x + nx * 600 + (-ny) * sideMag;
    bot.steerTargetRaw.y = head.y + ny * 600 + ( nx) * sideMag;
    return true;
  }

  private refreshFleeIfNeeded(bot: Snake, snakes: Snake[]): void {
    const head = bot.head;
    const tight = (AI.PERCEPTION_RANGE * 0.4) ** 2;
    const threatMult = this.threatMultFor(bot);
    for (let i = 0; i < snakes.length; i++) {
      const o = snakes[i]!;
      if (!o.alive || o.id === bot.id) continue;
      if (o.mass < bot.mass * threatMult) continue;
      const dx = o.head.x - head.x;
      const dy = o.head.y - head.y;
      if (dx * dx + dy * dy < tight) {
        bot.boosting = true;
        return;
      }
    }
  }

  private tryChase(bot: Snake, snakes: Snake[]): boolean {
    const brain = this.brains.get(bot.id);
    const head = bot.head;
    let prey: Snake | null = null;
    let preyDist2 = Infinity;
    const rangeSq = AI.PERCEPTION_RANGE * AI.PERCEPTION_RANGE;
    const chaseMult = this.chaseMultFor(bot);

    for (let i = 0; i < snakes.length; i++) {
      const o = snakes[i]!;
      if (!o.alive || o.id === bot.id) continue;
      if (o.mass > bot.mass / chaseMult) continue;
      const dx = o.head.x - head.x;
      const dy = o.head.y - head.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < preyDist2 && d2 < rangeSq) {
        preyDist2 = d2;
        prey = o;
      }
    }

    if (!prey) return false;

    // Predator-class behavior: if WE are massively bigger than the prey,
    // shift into "encircle" mode — aim at a point tangent to the prey's
    // current heading rather than directly at the head. The bot's body
    // sweeps in an arc that ends up surrounding the target, classic
    // Slither / Classic-Snake encirclement.
    //
    // Trigger: bot.mass > prey.mass * 2.0 AND prey is reasonably close.
    const massRatio = bot.mass / Math.max(1, prey.mass);
    const closeRange = (AI.PERCEPTION_RANGE * 0.55) ** 2;
    const veryClose  = (AI.PERCEPTION_RANGE * 0.30) ** 2;
    const shouldEncircle =
      massRatio > 2.0 &&
      preyDist2 > veryClose && // inside veryClose, just bite — no time to loop
      preyDist2 < rangeSq * 0.7;
    if (shouldEncircle && brain) {
      // Vector from prey → bot.
      const rx = head.x - prey.head.x;
      const ry = head.y - prey.head.y;
      const rLen = Math.hypot(rx, ry) || 1;
      // Perpendicular (rotate 90° in brain.encircleDir direction).
      const px = -ry / rLen * brain.encircleDir;
      const py =  rx / rLen * brain.encircleDir;
      // Encirclement radius scales with how big our body actually is —
      // a giant needs more room to loop than a mid-sized hunter.
      const loopR = Math.max(120, bot.radius * 6 + 60);
      // Target point: on the loop circle, ahead of the prey along the
      // tangent direction. Includes a small lead so we cut off escape.
      const lead = 0.5;
      const px2 = prey.head.x + Math.cos(prey.heading) * prey.speed() * lead;
      const py2 = prey.head.y + Math.sin(prey.heading) * prey.speed() * lead;
      bot.steerTargetRaw.x = px2 + px * loopR;
      bot.steerTargetRaw.y = py2 + py * loopR;
      // Don't burn boost while looping — saves mass for the kill bite.
      bot.boosting = false;
      return true;
    }

    // Standard chase — head straight at the prey with a heading-lead.
    const lead = 0.8;
    bot.steerTargetRaw.x = prey.head.x + Math.cos(prey.heading) * prey.speed() * lead;
    bot.steerTargetRaw.y = prey.head.y + Math.sin(prey.heading) * prey.speed() * lead;
    // Boost when prey is in close-strike range AND we can afford it.
    // Aggressive personalities boost-strike harder.
    const boostMassGate = bot.personality === 'aggressive' ? 450 : 600;
    bot.boosting = preyDist2 < closeRange && bot.mass > boostMassGate;
    return true;
  }

  private trySeekFood(bot: Snake, food: FoodField): boolean {
    const head = bot.head;
    const rangeSq = AI.PERCEPTION_RANGE * AI.PERCEPTION_RANGE;
    let best = -1;
    let bestScore = -Infinity;
    const pellets = food.pellets;
    const myHue = bot.teamHue;
    for (let i = 0; i < pellets.length; i++) {
      const p = pellets[i]!;
      const dx = p.x - head.x;
      const dy = p.y - head.y;
      const d2 = dx * dx + dy * dy;
      if (d2 > rangeSq) continue;
      const colorBoost = hueDistance(p.hue, myHue) <= 20 ? AI.COLOR_PREFERENCE : 1;
      const score = (p.mass * colorBoost) / (1 + d2 * 0.0001);
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0) return false;
    const p = pellets[best]!;
    bot.steerTargetRaw.x = p.x;
    bot.steerTargetRaw.y = p.y;
    bot.boosting = false;
    return true;
  }

  /**
   * Pick a wander destination. The old version always targeted map
   * center, which scattered bots toward the borders the moment they
   * couldn't see anything (PERCEPTION_RANGE = ~24% of map width, so a
   * bot in any corner saw nothing pulling it back). The new version
   * biases toward the *player*: most wanders pick a point inside a
   * `PLAYER_ORBIT` ring around the player so action density stays
   * concentrated where the camera is. A minority still picks map
   * center to avoid 100% of the world living on the player.
   *
   * Cautious bots get a bigger orbit ring so they don't all crowd the
   * player's exact tile — they roam the *neighborhood*, not the
   * player's hitbox.
   */
  private wander(bot: Snake, brain: AIBrainState, player: Snake): void {
    const head = bot.head;
    const distToPlayer = Math.hypot(player.head.x - head.x, player.head.y - head.y);
    // Force player-orbit when very far from the player — pulls a bot
    // that got stuck against an edge back into the action regardless of
    // the bias roll.
    const farFromPlayer = distToPlayer > AI.PLAYER_ORBIT * 1.5;
    const orbitPlayer = farFromPlayer || brain.rng() < AI.PLAYER_ORBIT_BIAS;
    if (orbitPlayer) {
      // Pick a random point on a ring of radius [0.5..1.0]*PLAYER_ORBIT
      // around the player. Cautious bots use a wider ring so they keep
      // their distance from the kill zone without leaving the area.
      const ringMin = AI.PLAYER_ORBIT * (bot.personality === 'cautious' ? 0.8 : 0.5);
      const ringMax = AI.PLAYER_ORBIT * (bot.personality === 'cautious' ? 1.3 : 1.0);
      const r = ringMin + brain.rng() * (ringMax - ringMin);
      const angle = brain.rng() * Math.PI * 2;
      bot.steerTargetRaw.x = clampInner(player.head.x + Math.cos(angle) * r);
      bot.steerTargetRaw.y = clampInner(player.head.y + Math.sin(angle) * r);
    } else {
      // Minority of wanders: drift toward map center so a tiny fraction
      // of bots still patrol the wider arena (visible on the minimap +
      // creates spawn-side activity for players in any quadrant).
      const cx = WORLD.SIZE * 0.5;
      const cy = WORLD.SIZE * 0.5;
      const jitter = 1200;
      bot.steerTargetRaw.x = clampInner(cx + (brain.rng() - 0.5) * jitter);
      bot.steerTargetRaw.y = clampInner(cy + (brain.rng() - 0.5) * jitter);
    }
    bot.boosting = false;
  }
}
